import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { queryClient } from '@/lib/queryClient';

type QueuedRequest = {
  resolve: (value: AxiosResponse) => void;
  reject: (reason?: unknown) => void;
  config: AxiosRequestConfig;
};

function isBrowser() {
  return typeof window !== 'undefined';
}

function redirectToLogin() {
  if (!isBrowser()) return;
  // Public customer links must not bounce to the staff login page.
  if (window.location.pathname.includes('/customer-view')) return;
  if (window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
}

function getBaseUrl() {
  return process.env.NEXT_PUBLIC_API_BASE_URL ?? 'https://appointments.codeclutch.tools/api/v1';
}

const apiClient: AxiosInstance = axios.create({
  baseURL: getBaseUrl(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let queue: QueuedRequest[] = [];

function processQueue(err: unknown) {
  const current = queue;
  queue = [];

  for (const item of current) {
    if (err) item.reject(err);
    else {
      apiClient(item.config).then(item.resolve).catch(item.reject);
    }
  }
}

function shouldSkipAuthRefresh(config?: AxiosRequestConfig) {
  const url = config?.url ?? '';
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/customer:verify-otp') ||
    url.includes('/auth/logout') ||
    url.includes('/auth/tokens:refresh') ||
    url.includes('/customer-view') ||
    url.includes('/availability') ||
    url.includes('/settings/customer-view') ||
    (config as any)?._skipAuthRefresh === true
  );
}

async function refreshTokens() {
  // Mark so interceptor won't try to refresh again if this call 401s.
  return apiClient.post('/auth/tokens:refresh', {}, { _skipAuthRefresh: true } as any);
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalConfig = error.config;
    const status = error.response?.status;

    if (!originalConfig || status !== 401 || shouldSkipAuthRefresh(originalConfig)) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<AxiosResponse>((resolve, reject) => {
        queue.push({ resolve, reject, config: originalConfig });
      });
    }

    isRefreshing = true;
    try {
      await refreshTokens();
      processQueue(undefined);
      return apiClient(originalConfig);
    } catch (refreshErr) {
      processQueue(refreshErr);
      queryClient.clear();
      redirectToLogin();
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

export default apiClient;

